import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import VedicBirthForm from "@/components/VedicBirthForm";
import VedicHowItWorks from "@/components/VedicHowItWorks";
import { Sparkles } from "lucide-react";

const IMG = "https://images.unsplash.com/photo-1729335511904-9b8690184935?crop=entropy&cs=srgb&fm=jpg&q=85";
const RASHI_IMG = "https://images.unsplash.com/photo-1652377755407-9b1cc4d48091?crop=entropy&cs=srgb&fm=jpg&q=85";

const RASHIS = [
  { name: "Mesha", en: "Aries", planet: "Mars", desc: "The ram. Ruled by Mars. Bold, direct, and quick to act. A natural pioneer." },
  { name: "Vrishabha", en: "Taurus", planet: "Venus", desc: "The bull. Ruled by Venus. Steady, patient, and loyal. A lover of comfort." },
  { name: "Mithuna", en: "Gemini", planet: "Mercury", desc: "The twins. Ruled by Mercury. Curious, witty, and expressive communicators." },
  { name: "Karka", en: "Cancer", planet: "Moon", desc: "The crab. Ruled by the Moon. Nurturing, intuitive, and deeply emotional." },
  { name: "Simha", en: "Leo", planet: "Sun", desc: "The lion. Ruled by the Sun. Confident, generous, and born to lead." },
  { name: "Kanya", en: "Virgo", planet: "Mercury", desc: "The maiden. Ruled by Mercury. Analytical, precise, and service-minded." },
  { name: "Tula", en: "Libra", planet: "Venus", desc: "The scales. Ruled by Venus. Balanced, charming, and drawn to harmony." },
  { name: "Vrishchika", en: "Scorpio", planet: "Mars", desc: "The scorpion. Ruled by Mars. Intense, secretive, and transformative." },
  { name: "Dhanu", en: "Sagittarius", planet: "Jupiter", desc: "The archer. Ruled by Jupiter. Optimistic, philosophical, and free-spirited." },
  { name: "Makara", en: "Capricorn", planet: "Saturn", desc: "The sea-goat. Ruled by Saturn. Disciplined, ambitious, and enduring." },
  { name: "Kumbha", en: "Aquarius", planet: "Saturn", desc: "The water-bearer. Ruled by Saturn. Visionary, independent, and humane." },
  { name: "Meena", en: "Pisces", planet: "Jupiter", desc: "The fishes. Ruled by Jupiter. Compassionate, imaginative, and spiritual." },
];

export function RashiSection() {
  const [showAll, setShowAll] = useState(false);
  return (
    <section className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-14 lg:py-20" data-testid="kundli-rashi-section">
      <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-accent mb-3">Rashis</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-semibold tracking-tight leading-tight">The twelve signs and their ruling planets</h2>
          <p className="mt-3 text-muted-foreground leading-relaxed max-w-lg">Each rashi carries a distinct energy. Learn the nature of your sign and the planet that rules it.</p>

          <div className="grid sm:grid-cols-2 gap-6 mt-8">
            {RASHIS.slice(0, 2).map((r) => (
              <div key={r.name} data-testid={`rashi-featured-${r.name}`}>
                <span className="h-11 w-11 grid place-items-center rounded-xl bg-gradient-to-br from-accent/20 to-primary/20 text-primary mb-3"><Sparkles className="h-5 w-5" /></span>
                <h3 className="font-serif text-lg font-semibold">{r.name} <span className="text-sm text-muted-foreground font-sans">({r.en})</span></h3>
                <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{r.desc}</p>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-3 mt-8">
            <Button variant="outline" onClick={() => setShowAll((v) => !v)} data-testid="view-all-rashi-btn" className="rounded-full border-accent text-primary hover:bg-accent/10">
              {showAll ? "Hide rashi list" : "View all rashi"}
            </Button>
          </div>
        </div>

        <div className="relative">
          <div className="absolute -inset-4 rounded-3xl bg-accent/10 blur-2xl" />
          <img src={RASHI_IMG} alt="Vedic zodiac wheel" className="relative rounded-2xl w-full h-72 lg:h-[26rem] object-cover shadow-lg" />
        </div>
      </div>

      {showAll && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-10 animate-float-up" data-testid="all-rashi-grid">
          {RASHIS.map((r) => (
            <div key={r.name} className="rounded-2xl border border-border bg-card p-5 hover-lift" data-testid={`rashi-card-${r.name}`}>
              <div className="flex items-center justify-between">
                <h3 className="font-serif text-lg font-semibold">{r.name}</h3>
                <span className="text-xs text-muted-foreground">{r.en}</span>
              </div>
              <p className="text-xs font-semibold uppercase tracking-wide text-accent mt-1">Ruled by {r.planet}</p>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{r.desc}</p>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function Result(r) {
  return (
    <div className="rounded-2xl border border-border bg-card p-6 space-y-5">
      <div className="grid grid-cols-3 gap-3 text-center">
        {[["Moon Sign", r.moon_sign], ["Nakshatra", r.nakshatra], ["Lagna", r.lagna]].map(([l, v]) => (
          <div key={l} className="rounded-xl bg-secondary/60 p-3"><p className="text-[10px] uppercase text-muted-foreground">{l}</p><p className="font-serif font-semibold text-sm mt-1 leading-tight">{v}</p></div>
        ))}
      </div>
      <div>
        <h4 className="font-serif font-semibold mb-2">Planetary Positions</h4>
        <div className="space-y-1.5">{r.planetary_positions.map((p) => (
          <div key={p.planet} className="flex justify-between text-sm border-b border-border/60 pb-1.5"><span>{p.planet}</span><span className="text-muted-foreground">{p.sign} · House {p.house}</span></div>
        ))}</div>
      </div>
      <div><h4 className="font-serif font-semibold mb-1.5">Personality</h4><p className="text-sm text-muted-foreground leading-relaxed">{r.personality}</p></div>
      <div><h4 className="font-serif font-semibold mb-1.5">Life Path</h4><p className="text-sm text-muted-foreground leading-relaxed">{r.life_path}</p></div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-2 border-t border-border">
        {[["Lucky No.", r.lucky.number], ["Colour", r.lucky.color], ["Day", r.lucky.day], ["Gemstone", r.lucky.gemstone]].map(([l, v]) => (
          <div key={l} className="text-center"><p className="text-[10px] uppercase text-muted-foreground">{l}</p><p className="font-semibold text-sm">{v}</p></div>
        ))}
      </div>
    </div>
  );
}

export default function Kundli() {
  return (
    <>
      <VedicBirthForm
        service="kundli" heroImg={IMG} sanskrit="॥ कुण्डली ॥" title="Understand your Rashi & Kundli"
        intro="Explore your zodiac sign, birth chart, planetary positions and personalised life insights based on your birth details."
        receive={["Detailed Rashi (moon sign) analysis", "Comprehensive Kundli chart", "Planetary positions across houses", "Personality traits & life path insights", "Lucky number, colour, day & gemstone"]}
        endpoint="/vedic/kundli" renderResult={Result}
      />
      <RashiSection />
      <VedicHowItWorks
        subtitle="Get your personalised Rashi & Kundli reading in four simple steps."
        steps={[
          { title: "Enter birth details", desc: "Share your name, date, time and place of birth." },
          { title: "Book online", desc: "Confirm your booking instantly with a verified reading." },
          { title: "Chart is mapped", desc: "Your Rashi, Nakshatra, Lagna and planetary houses are computed." },
          { title: "Receive your Kundli", desc: "Get your full chart with personality and life-path insights." },
        ]}
      />
    </>
  );
}
